'use client'

import { useEffect, useState } from 'react'
import { useSearchParams, usePathname, useRouter } from 'next/navigation'
import { ActionButton } from '@/components/ActionButton'
import { TextField } from '@/components/Fields'
import { useNotification } from '@/utils/notifications'
import { SupportLink } from './SupportLink'
import { apiBaseUrl } from '@/config'

declare global {
  interface Window {
    gtag?: (
      event: string,
      action: string,
      params: Record<string, string | undefined>
    ) => void
  }
}

type LeadFormProps = {
  title: string
  subtitle?: string
  callToAction: string
  successMessage: string
}

export function LeadForm({
  title,
  subtitle,
  callToAction,
  successMessage,
}: LeadFormProps) {
  const { showNotification } = useNotification()
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [company, setCompany] = useState('')

  const [formID, setFormID] = useState<string | null>(null)
  const [formSource, setFormSource] = useState<string | null>(null)
  const [formSourceLocation, setFormSourceLocation] = useState<string | null>(
    null
  )

  const searchParams = useSearchParams()
  const pathname = usePathname()

  useEffect(() => {
    const formIDValue = pathname?.split('/').pop() || null
    const formSourceValue = searchParams?.get('source') || null
    const formSourceLocationValue = searchParams?.get('source_location') || null

    setFormID(formIDValue)
    setFormSource(formSourceValue)
    setFormSourceLocation(formSourceLocationValue)

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'lead_form_start', {
        form_id: formIDValue ?? undefined,
        form_source: formSourceValue ?? undefined,
        form_source_location: formSourceLocationValue ?? undefined,
      })
    }
  }, [searchParams, pathname])

  const inputComplete = (value: string) => value.trim() !== ''

  const isNonPersonalEmailRegex =
    /^(?!.*@(?:gmail|icloud|outlook|hotmail|aol|yahoo)\.com).*$/
  const isEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const canSubmit =
    inputComplete(firstName) &&
    inputComplete(lastName) &&
    inputComplete(email) &&
    inputComplete(company) &&
    isNonPersonalEmailRegex.test(email) &&
    isEmailRegex.test(email) &&
    !loading

  const submitSignup = async () => {
    setLoading(true)
    try {
      const gaCustomEventParams = {
        form_id: formID ?? undefined,
        form_title: title,
        form_source: formSource ?? undefined,
        form_source_location: formSourceLocation ?? undefined,
      }

      const response = await fetch(`${apiBaseUrl}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          company,
          form: formID,
          source: formSource,
          source_location: formSourceLocation,
        }),
      })
      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string }
        throw new Error(errorData.message || 'Request failed')
      }
      showNotification({
        isError: false,
        title: 'Success',
        body: successMessage,
        timeout: 7500,
      })
      window.gtag?.('event', 'lead_form_submit', gaCustomEventParams)
      router.push('/')
    } catch (error) {
      showNotification({
        isError: true,
        title: 'Failed to sign up',
        body: (
          <>
            Something went wrong. Please try refreshing the page. If the error
            persists, <SupportLink />.
          </>
        ),
      })
    }
    setLoading(false)
  }

  return (
    <div className="max-w-[36rem] px-4">
      <h1 className="text-center text-3xl font-bold tracking-tight text-white">
        {title}
      </h1>
      {subtitle && (
        <p className="text-md mt-3 text-center text-white">{subtitle}</p>
      )}
      <dl className="my-10 border-t border-slate-800" />
      <form id={formID ?? ''} name={title}>
        <div className="grid grid-cols-2 gap-6">
          <TextField
            label="First Name"
            id="first_name"
            setValue={setFirstName}
            value={firstName}
          />
          <TextField
            label="Last Name"
            id="last_name"
            setValue={setLastName}
            value={lastName}
          />
          <TextField
            className="col-span-full"
            label="Work Email"
            id="email"
            setValue={setEmail}
            value={email}
            errorMessage={
              email && !isNonPersonalEmailRegex.test(email)
                ? 'Please use your company email address.'
                : undefined
            }
          />
          <TextField
            className="col-span-full"
            label="Company Name"
            id="company"
            setValue={setCompany}
            value={company}
          />
          <div className="col-span-full">
            <ActionButton
              onClick={submitSignup}
              loading={loading}
              enabled={canSubmit}
              width="w-full"
            >
              {callToAction}
            </ActionButton>
          </div>
        </div>
      </form>
    </div>
  )
}
